// src/components/views/InferenceLoop/InferenceLoop.tsx

import React, { useEffect, useState } from 'react'
import { useAppSelector, useAppDispatch } from '../../../store/hooks'
import { useGetModelQuery } from '../../../services/modelsApi'
import { selectSelectedProjectId, selectShowingNewAAL } from '../../../store/slices/projectSlice'
import { useGetProjectQuery } from '../../../services/projectsApi'
import { selectSelectedModelId, handleProjectChange } from '../../../store/slices/modelSlice'
import styled from 'styled-components'
import { FaSync, FaCheckCircle, FaPlay, FaEye, FaCog, FaCompressArrowsAlt } from 'react-icons/fa'
import ComparePhase from './components/ComparePhase'
import RunPhase from './components/RunPhase'
import ReviewPhase from './components/ReviewPhase'
import FineTunePhase from './components/FineTunePhase'

const ViewContainer = styled.div`
  height: 100%;
  display: flex;
  flex-direction: column;
  background: #1a1a1a;
  padding: 0.5rem;
  gap: 0.75rem;
  overflow: visible;
`

const WorkflowContainer = styled.div`
  background: #2a2a2a;
  border-radius: 12px;
  padding: 0.25rem;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(147, 51, 234, 0.1);
  flex-shrink: 0;
`

const PhaseIndicator = styled.div`
  display: flex;
  gap: 0.25rem;
  position: relative;
  background: rgba(0, 0, 0, 0.2);
  border-radius: 8px;
  padding: 0.25rem;

  @media (max-width: 768px) {
    flex-direction: column;
    gap: 0.125rem;
  }
`

const Phase = styled.div<{
  $active?: boolean
  $completed?: boolean
  $disabled?: boolean
}>`
  flex: 1;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  border-radius: 6px;
  background: ${(props) => {
    if (props.$active)
      return 'linear-gradient(135deg, rgba(147, 51, 234, 0.2) 0%, rgba(147, 51, 234, 0.1) 100%)'
    if (props.$completed)
      return 'linear-gradient(135deg, rgba(16, 185, 129, 0.15) 0%, rgba(16, 185, 129, 0.05) 100%)'
    return 'transparent'
  }};
  border: 1px solid
    ${(props) => {
      if (props.$active) return 'rgba(147, 51, 234, 0.4)'
      if (props.$completed) return 'rgba(16, 185, 129, 0.3)'
      return 'transparent'
    }};
  cursor: ${(props) => (props.$disabled ? 'not-allowed' : 'pointer')};
  transition: all 0.2s ease;
  position: relative;
  color: ${(props) => {
    if (props.$disabled) return 'rgba(255, 255, 255, 0.3)'
    if (props.$active) return '#ffffff'
    if (props.$completed) return '#10b981'
    return 'rgba(255, 255, 255, 0.8)'
  }};
  opacity: ${(props) => (props.$disabled ? 0.5 : 1)};
  min-height: 36px;

  &:hover {
    background: ${(props) => {
      if (props.$disabled) return 'transparent'
      if (props.$active)
        return 'linear-gradient(135deg, rgba(147, 51, 234, 0.25) 0%, rgba(147, 51, 234, 0.15) 100%)'
      if (props.$completed)
        return 'linear-gradient(135deg, rgba(16, 185, 129, 0.2) 0%, rgba(16, 185, 129, 0.1) 100%)'
      return 'rgba(255, 255, 255, 0.05)'
    }};
    border-color: ${(props) => {
      if (props.$disabled) return 'transparent'
      if (props.$active) return 'rgba(147, 51, 234, 0.6)'
      if (props.$completed) return 'rgba(16, 185, 129, 0.5)'
      return 'rgba(147, 51, 234, 0.3)'
    }};
  }

  @media (max-width: 768px) {
    justify-content: center;
  }
`

const PhaseIconContainer = styled.div<{
  $active?: boolean
  $completed?: boolean
  $disabled?: boolean
}>`
  width: 20px;
  height: 20px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${(props) => {
    if (props.$active) return 'linear-gradient(135deg, #9333ea 0%, #c084fc 100%)'
    if (props.$completed) return 'linear-gradient(135deg, #10b981 0%, #34d399 100%)'
    if (props.$disabled) return 'rgba(255, 255, 255, 0.1)'
    return 'rgba(255, 255, 255, 0.15)'
  }};
  font-size: 0.75rem;
  color: ${(props) => {
    if (props.$disabled) return 'rgba(255, 255, 255, 0.3)'
    return '#ffffff'
  }};
  flex-shrink: 0;
  transition: all 0.2s ease;
`

const PhaseContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  flex: 1;
  min-width: 0;

  @media (max-width: 768px) {
    justify-content: center;
  }
`

const PhaseTitle = styled.h3`
  font-size: 0.8rem;
  font-weight: 500;
  margin: 0;
  color: inherit;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`

const PhaseStatus = styled.div<{
  $active?: boolean
  $completed?: boolean
  $disabled?: boolean
}>`
  font-size: 0.625rem;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  color: ${(props) => {
    if (props.$active) return '#9333ea'
    if (props.$completed) return '#10b981'
    if (props.$disabled) return 'rgba(255, 255, 255, 0.3)'
    return 'rgba(255, 255, 255, 0.5)'
  }};
  white-space: nowrap;

  @media (max-width: 768px) {
    display: none;
  }
`

const ContentContainer = styled.div`
  flex: 1;
  background: #1a1a1a;
  border-radius: 12px;
  padding: 1rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  position: relative;
  overflow: visible;
  min-height: 0;
  display: flex;
  flex-direction: column;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 3px;
    background: linear-gradient(90deg, #9333ea 0%, #c084fc 100%);
  }
`

const PhaseContentWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  padding-top: 0.5rem;
`

const StubView = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100%;
  color: #8c8c8c;
  font-size: 1.2rem;
  gap: 1rem;

  .loading-icon {
    font-size: 2rem;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`

const InferenceLoop: React.FC = (): JSX.Element => {
  const [currentPhase, setCurrentPhase] = useState(1) // Always default to Run phase
  const [completedPhases] = useState(new Set<number>())
  const [pollingInterval, setPollingInterval] = useState(0)

  // Track previous model ID to detect changes
  const prevModelIdRef = React.useRef<string | undefined>(undefined)

  const selectedProjectId = useAppSelector(selectSelectedProjectId)
  const selectedModelId = useAppSelector(selectSelectedModelId)
  const showingNewAAL = useAppSelector(selectShowingNewAAL)
  const dispatch = useAppDispatch()

  const {
    data: project,
    isLoading: projectLoading,
    error: projectError,
  } = useGetProjectQuery(selectedProjectId || '', {
    skip: !selectedProjectId,
  })

  const {
    data: selectedModel,
    isLoading: modelLoading,
    error: modelError,
  } = useGetModelQuery(selectedModelId || '', {
    skip: !selectedModelId,
    pollingInterval,
  })

  // Update polling interval based on model state
  useEffect(() => {
    if (
      ['training', 'stopping'].includes(selectedModel?.state.toLowerCase() || '') ||
      ['creating', 'stopping', 'deleting'].includes(
        selectedModel?.deployment?.status.toLowerCase() || ''
      )
    ) {
      setPollingInterval(2500) // Poll every 2.5 seconds when training
    } else {
      setPollingInterval(0) // Stop polling when not training
    }
  }, [selectedModel?.deployment?.status, selectedModel?.state])

  // Restore model selection when project changes
  useEffect(() => {
    if (selectedProjectId) {
      dispatch(handleProjectChange(selectedProjectId))
    }
  }, [selectedProjectId, dispatch])

  // Function to check if a phase should be enabled based on batch status and dataset config
  const isPhaseEnabled = React.useCallback(
    (phaseId: number): boolean => {
      // Run phase is always enabled
      if (phaseId === 1) return true

      // Compare phase is always enabled for bounding box projects
      if (
        phaseId === 4 &&
        (project?.annotation_type === 'bounding_box' ||
          project?.annotation_type === 'oriented_bounding_box')
      )
        return true

      // Review and Fine-Tune phases (2 and 3) are enabled ONLY if:
      // 1. NOT currently configuring a new AAL
      // 2. A batch inference has been run (batch object exists)
      // 3. The batch has completed successfully
      // 4. There are actual results from the batch
      if (phaseId === 2 || phaseId === 3) {
        // If configuring new AAL, these phases are disabled
        if (showingNewAAL) return false

        const batch = selectedModel?.batch
        if (!batch) return false // No batch has been run

        const batchStatus = batch.status
        const batchComplete = batchStatus === 'COMPLETE' || batchStatus === 'COMPLETE_WITH_ERR'

        // Also check that the batch actually processed some content
        const hasResults = batch.total_content ? batch.total_content > 0 : false

        return batchComplete && hasResults
      }

      return false
    },
    [project?.annotation_type, showingNewAAL, selectedModel?.batch]
  )

  // Redirect to Run phase if current phase becomes disabled due to batch status change or model change
  // Also handles model switching - if switching to a model with no AAL history, reset to Run phase
  useEffect(() => {
    if (!isPhaseEnabled(currentPhase)) {
      setCurrentPhase(1) // Redirect to Run phase
    }
  }, [currentPhase, isPhaseEnabled])

  // Robustly handle model switching by tracking previous model ID
  useEffect(() => {
    // Check if model actually changed (and we had a previous model)
    if (prevModelIdRef.current !== undefined && prevModelIdRef.current !== selectedModelId) {
      setCurrentPhase(1) // Force reset to Run phase when model changes
    }

    // Update the ref with current model ID
    prevModelIdRef.current = selectedModelId
  }, [selectedModelId])

  const phases = React.useMemo(() => {
    const basePhases = [
      {
        id: 1,
        icon: FaPlay,
        label: 'Run Inference',
        description: 'AI model automatically labels your data using current model predictions',
        status: 'Generate Labels',
      },
      {
        id: 2,
        icon: FaEye,
        label: 'Review & Correct',
        description:
          'Human-in-the-Adaptive Active Labeling review and correction of AI-generated labels',
        status: 'Quality Control',
      },
    ]

    if (
      project?.annotation_type === 'bounding_box' ||
      project?.annotation_type === 'oriented_bounding_box'
    ) {
      return [
        ...basePhases,
        {
          id: 3,
          icon: FaCog,
          label: 'Fine-Tune Model',
          description: 'Train improved model version using corrected labels',
          status: 'Model Training',
        },
        {
          id: 4,
          icon: FaCompressArrowsAlt,
          label: 'Compare Results',
          description: 'Analyze performance improvements and model evolution',
          status: 'Performance Analysis',
        },
      ]
    }

    return basePhases
  }, [project?.annotation_type])

  // Render the appropriate view based on the current phase
  const renderPhaseContent = (): JSX.Element | null => {
    // Show loading state
    if (projectLoading || modelLoading) {
      return (
        <StubView>
          <FaSync className="loading-icon" />
          <span>Loading workflow...</span>
        </StubView>
      )
    }

    // Show error state
    if (projectError || modelError) {
      return (
        <StubView>
          <span>
            Error: {(projectError || (modelError as any))?.data?.message || 'An error occurred'}
          </span>
        </StubView>
      )
    }

    switch (currentPhase) {
      case 1: // Run
        return <RunPhase />

      case 2: // Review
        return <ReviewPhase />

      case 3: // Fine-Tune
        return <FineTunePhase />

      case 4: // Compare
        return <ComparePhase />

      default:
        return <RunPhase />
    }
  }

  // Function to handle phase click with disabled state check
  const handlePhaseClick = (phaseId: number) => {
    if (isPhaseEnabled(phaseId)) {
      setCurrentPhase(phaseId)
    }
  }

  // Get phase status text
  const getPhaseStatusText = (phase: any, phaseId: number): string => {
    if (!isPhaseEnabled(phaseId)) return 'Pending'
    if (completedPhases.has(phaseId)) return 'Completed'
    if (currentPhase === phaseId) return 'Active'
    return phase.status
  }

  return (
    <ViewContainer>
      <WorkflowContainer>
        <PhaseIndicator>
          {phases.map((phase, index) => (
            <Phase
              key={phase.id}
              $active={currentPhase === phase.id}
              $completed={completedPhases.has(phase.id)}
              $disabled={!isPhaseEnabled(phase.id)}
              onClick={() => handlePhaseClick(phase.id)}
            >
              <PhaseIconContainer
                $active={currentPhase === phase.id}
                $completed={completedPhases.has(phase.id)}
                $disabled={!isPhaseEnabled(phase.id)}
              >
                {completedPhases.has(phase.id) ? <FaCheckCircle /> : <phase.icon />}
              </PhaseIconContainer>

              <PhaseContent>
                <PhaseTitle>{phase.label}</PhaseTitle>
                <PhaseStatus
                  $active={currentPhase === phase.id}
                  $completed={completedPhases.has(phase.id)}
                  $disabled={!isPhaseEnabled(phase.id)}
                >
                  {getPhaseStatusText(phase, phase.id)}
                </PhaseStatus>
              </PhaseContent>
            </Phase>
          ))}
        </PhaseIndicator>
      </WorkflowContainer>

      <ContentContainer>
        <PhaseContentWrapper>{renderPhaseContent()}</PhaseContentWrapper>
      </ContentContainer>
    </ViewContainer>
  )
}

export default InferenceLoop
